import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, DataTable, ActionButton } from "./ui";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MDBContainer, MDBTooltip } from "mdb-react-ui-kit";
import { api } from "../services";
import Toolbar from "./Toolbar";
import { useTranslation } from "react-i18next";
import ConfirmModal from "./Modals/ConfirmModal";
import { toast } from "react-toastify";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import {
  resolveDateLocale,
  formatLocalizedDate,
  formatLocalizedTime,
} from "../utils/formatDateTime";

function ManageBranches() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const dateLocale = resolveDateLocale(i18n.language);
  const { can } = usePermission();
  const canAddBranch = can(RBAC_MODULES.SETTINGS, PERMISSION_ACTIONS.ADD);
  const canViewBranchUi = can(RBAC_MODULES.SETTINGS, PERMISSION_ACTIONS.VIEW);
  const canDeleteBranch = can(RBAC_MODULES.SETTINGS, PERMISSION_ACTIONS.DELETE);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { storeId: stateStoreId, storeName: stateStoreName } = location.state || {};
  const storeIdFromQuery = searchParams.get("storeId") || "";
  const storeId = stateStoreId || storeIdFromQuery || undefined;
  const storeName = stateStoreName || searchParams.get("storeName") || "";
  const [branches, setBranches] = useState([]);
  const [admin, setAdmin] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [apps, setApps] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableSortColumn, setTableSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedBranch]);

  const handleBranchSelect = (branch) => {
    if (branch && branch._id) {
      setSelectedBranch(branch);
    } else {
      setSelectedBranch(null);
    }
  };

  const [modalData, setModalData] = useState({
    title: "",
    message: "",
    action: () => {},
    actionData: null,
    visibility: null,
  });

  useEffect(() => {
    if (storeId) fetchStoreDetails();
  }, [storeId]);

  useEffect(() => {
    if (storeId) fetchApps();
  }, [storeId]);

  /** Normalize GET /stores/:id — Updated API uses { success, data: store }; legacy returned the store root. */
  const extractStorePayload = (res) => {
    const d = res?.data;
    const inner = d?.data;
    if (inner != null && typeof inner === 'object') return inner;
    if (d != null && typeof d === 'object') return d;
    return {};
  };

  const fetchStoreDetails = async () => {
    if (!storeId) return;
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const store = extractStorePayload(response);
      setBranches(store.branches || []);
      setAdmin(store.admin || {});
    } catch (error) {
      console.error(
        "Error fetching store details:",
        error.response?.data || error.message
      );
    }
  };

  const fetchApps = async () => {
    if (!storeId) return;
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/store-apps/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Apps data:", response.data.apps);
      setApps(response.data.apps || []);
    } catch (err) {
      console.error("Error fetching apps:", err.message);
    }
  };

  const handleAddBranch = () => {
    navigate("/add-branch", { state: { storeId, storeName } });
  };

  const handleViewBranch = (branchId) => {
    navigate("/view-branch-v1", { state: { storeId, branchId } });
  };

  const filteredBranches = branches.filter((branch) => {
    if (selectedBranch && branch._id !== selectedBranch._id) {
      return false;
    }
    const query = searchQuery.toLowerCase();
    const possibleNames = [
      branch?.name?.ar,
      branch?.name_ar,
      branch?.name?.en,
      typeof branch?.name === 'string' ? branch?.name : ''
    ];
    return possibleNames.some(n => n && n.toLowerCase().includes(query));
  });

  const sortedBranches = useMemo(() => {
    const list = [...filteredBranches];
    const dir = sortDirection === "asc" ? 1 : -1;
    const getBranchName = (branch) =>
      isArabic
        ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name || ""
        : branch?.name?.en || branch?.name || "";

    const getSortValue = (row, column) => {
      switch (column) {
        case "name":
          return getBranchName(row).toLowerCase();
        case "phone":
          return (row?.phone || "").toLowerCase();
        case "address":
          return (row?.address || "").toLowerCase();
        case "createdAt":
          return row.createdAt ? new Date(row.createdAt).getTime() : 0;
        default:
          return "";
      }
    };

    list.sort((a, b) => {
      const av = getSortValue(a, tableSortColumn);
      const bv = getSortValue(b, tableSortColumn);
      if (typeof av === "string" && typeof bv === "string") {
        return dir * av.localeCompare(bv);
      }
      return dir * (av < bv ? -1 : av > bv ? 1 : 0);
    });

    return list;
  }, [filteredBranches, tableSortColumn, sortDirection, isArabic]);

  const applyTableSort = (column, direction) => {
    setTableSortColumn(column);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const indexOfLastItem = currentPage * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const currentItems = sortedBranches.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getLatestSuccessfulSync = useCallback((branch) => {
    const syncedApps = branch.syncedApps || [];

    const successfulSyncs = syncedApps.filter((sync) => {
      return apps.some((app) => app.application === sync.app);
    });

    const syncsByApp = successfulSyncs.reduce((acc, sync) => {
      if (
        !acc[sync.app] ||
        new Date(sync.synced_date) > new Date(acc[sync.app].synced_date)
      ) {
        acc[sync.app] = {
          ...sync,
          isIntegrated: true,
          appDetails: apps.find((a) => a.application === sync.app),
        };
      }
      return acc;
    }, {});

    return Object.values(syncsByApp).sort((a, b) => {
      if (a.isIntegrated && !b.isIntegrated) return -1;
      if (!a.isIntegrated && b.isIntegrated) return 1;
      return a.app.localeCompare(b.app);
    });
  }, [apps]);

  const handleDeleteBranch = useCallback(async (branchId) => {
    try {
      const token = localStorage.getItem("token");
      const payload = {
        branchId,
      };
      const response = await api.post(`/branch`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status >= 200 && response.status < 300) {
        toast.success(
          response.data.message || `${t("Branch deleted successfully.")}`
        );
        fetchStoreDetails();
      } else {
        toast.error(`${t("Failed to delete branch.")}`);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Error deleting branch. Please try again."
      );
      console.error(error);
    }
  }, [t]);

  const branchColumns = useMemo(
    () => [
      {
        id: "name",
        header: t("branch"),
        sortable: true,
        strong: true,
        render: (branch) =>
          isArabic
            ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
            : branch?.name?.en || branch?.name,
      },
      {
        id: "phone",
        header: t("phone"),
        sortable: true,
        render: (branch) => branch?.phone || "N/A",
      },
      {
        id: "address",
        header: t("address"),
        sortable: true,
        className: "oh-table-td--wrap",
        render: (branch) => (
          <span style={{ whiteSpace: "normal", maxWidth: "150px", lineHeight: "1.4", display: "inline-block" }}>
            {branch?.address || "N/A"}
          </span>
        ),
      },
      {
        id: "user",
        header: t("user"),
        render: (branch) => (
          <>
            <strong>{admin.username}</strong>
            <br />
            <small>Super Admin</small>
            <br />
            <small>{branch.email}</small>
          </>
        ),
      },
      {
        id: "createdAt",
        header: t("createdAt"),
        sortable: true,
        render: (branch) =>
          branch.createdAt ? (
            <div className="d-flex flex-column" dir="ltr" style={{ textAlign: isArabic ? "right" : "left" }}>
              <span>{formatLocalizedDate(branch.createdAt, dateLocale)}</span>
              <span>
                {formatLocalizedTime(branch.createdAt, dateLocale, {
                  withSeconds: true,
                })}
              </span>
            </div>
          ) : (
            "-"
          ),
      },
      {
        id: "actions",
        header: t("actions"),
        render: (branch) => {
          const canDelete =
            canDeleteBranch && getLatestSuccessfulSync(branch).length === 0;
          return (
            <div
              className="d-flex gap-2 align-items-center flex-nowrap oh-table-actions"
              onClick={(e) => e.stopPropagation()}
            >
              {canViewBranchUi ? (
                <ActionButton
                  variant="view"
                  onClick={() => handleViewBranch(branch._id)}
                >
                  {t("view")}
                </ActionButton>
              ) : (
                <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                  <span className="d-inline-flex">
                    <ActionButton variant="view" disabled>
                      {t("view")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              )}
              {canDelete ? (
                <ActionButton
                  variant="delete"
                  onClick={() => {
                    setModalData({
                      title: "Confirm Action",
                      message: `${t(
                        "Are you sure you want to delete this branch? This action cannot be undone."
                      )}`,
                      action: () => handleDeleteBranch(branch._id),
                      actionData: branch._id,
                      visibility: false,
                    });
                    setShowConfirm(true);
                  }}
                >
                  {t("delete")}
                </ActionButton>
              ) : canDeleteBranch ? (
                <MDBTooltip
                  tag="span"
                  title={t(
                    "Branches with synced apps cannot be deleted",
                    "Cannot delete a branch that has synced apps"
                  )}
                >
                  <span className="d-inline-flex">
                    <ActionButton variant="delete" disabled>
                      {t("delete")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : (
                <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                  <span className="d-inline-flex">
                    <ActionButton variant="delete" disabled>
                      {t("delete")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              )}
            </div>
          );
        },
      },
    ],
    [t, isArabic, dateLocale, admin.username, canDeleteBranch, canViewBranchUi, getLatestSuccessfulSync, handleDeleteBranch, handleViewBranch]
  );

  if (!storeId) {
    return (
      <MDBContainer fluid className="content-area flex-grow-1">
        <h2 style={{ color: "#030F1B", fontWeight: "600" }}>{t("branches")}</h2>
        <p style={{ color: "#444", maxWidth: 520 }}>
          {t(
            "Select a store from Store & Branch overview first, or open this page with ?storeId= in the URL."
          )}
        </p>
        <Button
          className="ocean"
          style={{ marginTop: 12 }}
          onClick={() => navigate("/store&branches-overview")}
        >
          {t("Go to store & branches overview")}
        </Button>
      </MDBContainer>
    );
  }

  return (
    <div className="manage-branches-page-root oh-page">
    <MDBContainer fluid className="content-area flex-grow-1">
      <h1 className="page-title-header">
        {(storeName && String(storeName).trim()) || t("Store")} — {t("branches")}
      </h1>
      <p className="oh-page-subtitle" style={{ marginTop: -16 }}>
        {t("Store ID")}: {storeId}
      </p>

      <div className="oh-page-section">
        <Toolbar
          showSearch={true}
          searchPlaceHolder={t("searchBranchByName")}
          searchQuery={searchQuery}
          handleSearch={(e) => setSearchQuery(e.target.value)}
          showBranchDropdown={true}
          options={branches}
          handleSelect={handleBranchSelect}
          selectedOption={selectedBranch}
          placeholder={t("selectbranchmanagebranches")}
          backgroundColor="#f9f9f9"
          hideSearchIcon={true}
          searchFirst={true}
          branchLabel="branch"
          showBranchIcon={false}
          fromManageBranches={true}
        />
      </div>

      <div className="oh-page-section">
      <DataTable
        tableId="manage-branches"
        title={t("branches")}
        columns={branchColumns}
        data={currentItems}
        rowKey="_id"
        emptyState={t("noBranchesToDisplay")}
        isRtl={isArabic}
        selectable
        selectionMode="checkbox"
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        bulkBarPlacement="end"
        selectedLabel={t("Selected", { defaultValue: "Selected" })}
        selectAllLabel={t("Select All", { defaultValue: "Select All" })}
        actionDropdownLabel={t("Bulk Action", { defaultValue: "Bulk Action" })}
        primaryAction={
          canAddBranch
            ? { label: t("addnewbranch"), onClick: handleAddBranch }
            : {
                label: t("addnewbranch"),
                onClick: handleAddBranch,
                disabled: true,
                title: t("noPermissionToAccessThis"),
              }
        }
        sort={{
          column: tableSortColumn,
          direction: sortDirection,
          onChange: ({ column, direction }) => applyTableSort(column, direction),
        }}
        onRowClick={
          canViewBranchUi
            ? (branch) => handleViewBranch(branch._id)
            : undefined
        }
        pagination={{
          page: currentPage,
          pageSize,
          total: sortedBranches.length,
          onPageChange: handlePageChange,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          showLabel: t("show"),
          ofLabel: t("of"),
          resultsLabel: t("results", { defaultValue: "results" }),
          isRtl: isArabic,
          previousLabel: t("previous"),
          nextLabel: t("next"),
        }}
      />
      </div>
      <ConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title={modalData.title}
        message={modalData.message}
        action={modalData.action}
        actionData={modalData.actionData}
        visibility={modalData.visibility}
      />
    </MDBContainer>
    </div>
  );
}

export default ManageBranches;
